import { CSSProperties, useEffect, useId, useMemo } from 'react';

/**
 * Responsive scrolling strategy for dialog content using container queries.
 *
 * This solution optimizes user experience across varying viewport heights:
 *
 * - On tall screens: Promotes the inner list area (scroller) to become the scrollable region.
 *   Dialog chrome (header, controls) remains fixed; only the list scrolls, so context is kept in view.
 *
 * - On short screens: Avoids forcing the list into a tiny inner scrolling area,
 *   and instead allows the entire dialog (header, controls, list) to flow as a single long scroll surface.
 *   This prevents usability issues from very small, inner-scrollable areas.
 *
 * Implementation notes:
 * - Each component instance injects its own <style> tag.
 * - Styles are updated automatically when the breakpoint changes.
 * - CSS container queries determine when to switch between 'inner' and 'outer' scrolling.
 * - Because CSS variables do not work in @container queries, pixel values are injected dynamically.
 *
 * Returns:
 *   - containerStyle: Apply to the dialog container element (e.g., FormDialogContent or DialogContentLayout)
 *   - scrollerClassName: Apply to DialogBody's scrollerClassName prop
 *   - bodyClassName: Apply to DialogBody's className prop
 *
 * @param breakpoint Number (pixels): Minimum dialog body height for toggling to inner scroll region
 *
 * @example
 * const responsive = useResponsiveDialogBody(550);
 *
 * <FormDialogContent
 *   style={responsive.containerStyle}
 *   renderDialogBody={children => (
 *     <DialogBody
 *       className={responsive.bodyClassName}
 *       scrollerClassName={responsive.scrollerClassName}
 *     >
 *       {children}
 *     </DialogBody>
 *   )}
 * />
 *
 * Potential optimization:
 *   This container query approach bases logic on the *parent* dialog body height.
 *   For smarter behavior (e.g. disabling inner scroll when the list would be <100px tall),
 *   container queries tied to the inner scrolled region's size would be ideal; this is not yet possible via pure CSS.
 */
export function useResponsiveDialogBody(breakpoint: number) {
  const componentId = useId();
  const styleId = `responsive-dialog-${componentId}`;
  const uniqueClass = `responsive-dialog-${componentId.replace(/:/g, '-')}`;

  // Inject or update dedicated style tag for this component
  useEffect(() => {
    let styleTag = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleTag) {
      // Create new style tag
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    // Update style content with current breakpoint
    styleTag.textContent = `
      .${uniqueClass}-body {
        @container dialog-body (height < ${breakpoint}px) {
          min-height: 0;
        }
        
        @container dialog-body (height >= ${breakpoint}px) {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
      }
      
      .${uniqueClass}-scroller {
        @container dialog-body (height >= ${breakpoint}px) {
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
      }
    `;

    return () => {
      // Clean up style tag on unmount
      styleTag?.remove();
    };
  }, [breakpoint, componentId, styleId, uniqueClass]);

  return useMemo(() => {
    const containerStyle: CSSProperties = {
      containerType: 'size',
      containerName: 'dialog-body'
    };

    return {
      // For FormDialogContent or DialogContentLayout
      containerStyle,

      // For DialogBody scrollerClassName
      scrollerClassName: `${uniqueClass}-scroller`,

      // For DialogBody className
      bodyClassName: `${uniqueClass}-body`
    };
  }, [uniqueClass]);
}
